import type { TableColumns } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType } from '@pmp/api';

import { advisersCRColumns, assistantsCRColumns } from '../brokers/brokerCRConfig';
import { contactDetailsCRColumns, operationsWhitelistCRColumns, tradingAddressCRColumns } from '../firms/lenderFirmCRConfig';

export type SubTableRow = Record<string, unknown>;

/**
 * A collection that travels inside a change request row and that the change request screen shows in a
 * tab of its own: trading addresses, operations, assistants, advisers. The log names its fields with
 * that screen's columns, so a column added there is named here too.
 */
export type SubTable = {
  /** The column that names an item of the collection, shown on every row of it in the detail. */
  rowLabel: string;
  /** i18n key for the collection, which is what the change request screen's tab calls it. */
  title: string;
  columns: TableColumns<SubTableRow>;
};

/** Keyed by the field the collection travels in, which is the field the log reports it under. */
export const SUB_TABLES: Partial<Record<ActionEntityType, Record<string, SubTable>>> = {
  [ActionEntityType.LENDER_FIRMS]: {
    tradingAddresses: {
      rowLabel: 'name',
      title: 'lender.firms.labels.tradingNames',
      columns: tradingAddressCRColumns as unknown as TableColumns<SubTableRow>,
    },
    operationsWhitelist: {
      rowLabel: 'type',
      title: 'lender.firms.labels.operationsWhitelist',
      columns: operationsWhitelistCRColumns as unknown as TableColumns<SubTableRow>,
    },
  },
  [ActionEntityType.BROKERS]: {
    assistants: {
      rowLabel: 'displayName',
      title: 'lender.brokers.labels.assistants',
      columns: assistantsCRColumns as unknown as TableColumns<SubTableRow>,
    },
    advisers: {
      rowLabel: 'displayName',
      title: 'lender.brokers.labels.advisers',
      columns: advisersCRColumns as unknown as TableColumns<SubTableRow>,
    },
  },
};

/** What the record's own fields are called, so the sections above the collections have a heading too. */
export const RECORD_SECTION_TITLES: Partial<Record<ActionEntityType, string>> = {
  [ActionEntityType.LENDER_FIRMS]: 'lender.firms.labels.firmTitle',
  [ActionEntityType.BROKERS]: 'lender.brokers.labels.intermediary',
};

/**
 * The record's own fields that the change request screen names in a tab of its own rather than in its
 * main grid, so the log names them too instead of falling back to the model's field name.
 */
export const RECORD_EXTRA_COLUMNS: Partial<Record<ActionEntityType, TableColumns<SubTableRow>>> = {
  [ActionEntityType.LENDER_FIRMS]: contactDetailsCRColumns as unknown as TableColumns<SubTableRow>,
};

export const getSubTables = (entityType: ActionEntityType | null | undefined): Record<string, SubTable> =>
  (entityType && SUB_TABLES[entityType]) || {};

/**
 * The value lists the collections' columns render with. Without them a cell shows the stored code,
 * `ADVICE` where the change request screen says `Advice`.
 */
export const SUB_TABLE_LIST_NAMES: FieldType[] = [
  ...new Set(
    Object.values(SUB_TABLES)
      .flatMap(subTables => Object.values(subTables))
      .flatMap(subTable => subTable.columns.map(column => column.options?.listName))
      .filter((listName): listName is FieldType => !!listName),
  ),
];
